import { useEffect, useState } from 'react';
import { Button, Collapse, Form, message } from 'antd';
import ReprojectionMenuDetails from './conversionMenuData/ReprojectionMenuDetails';
import AdvanceMenuDetails from './conversionMenuData/AdvanceMenuDetails';
import GeometryMenuDetails from './conversionMenuData/GeometryMenuDetails';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { ConversionFormDetails } from './conversionType';
import { DataIndexMenuEditModalContainer } from '../conversionMenu.style';
import { DataIndexItem } from '../../../types/interfaces';
import { loadDataItem, processData, updateParams } from '../../tables/dataIndexTable/DataIndexLambdaUtils';
import { updateItemStore } from '../../../store/DataStore';
const { Panel } = Collapse;

interface ButtonItem {
    id: number;
    label: string;
}

const conversionMenuButtons: ButtonItem[] = [
    { id: 1, label: 'Reprojection' },
    { id: 2, label: 'Geometry' },
    { id: 3, label: 'Advance' },
];

interface ConversionDetailsProps {
    onCancel: () => void;
    data: DataIndexItem;
    onChanged: (dataId: string) => void;
}
const initialConversionDetails: ConversionFormDetails = {
    reprojection: {
        in_srs: "",
        out_srs: "",
        in_geoid: "",
        out_geoid: ""
    },
    proj_pipeline: "",
    simplify: false,
    noise_removal: false,
    force_pdal: false,
    translate: [0, 0, 0], // Replace 0 with your default values for dx, dy, dz
    polygon_bounds: ""
};

const ConversionDetails = ({ onCancel, data, onChanged }: ConversionDetailsProps) => {

    const params = data.params;

    // First catalog
    const isReprojectionEmpty = params?.reprojection
        ? Object.values(params.reprojection).every(value => value === "" || value === 'None')
        : true;
    const selectedButtonsFromParams = []
    
    if (!isReprojectionEmpty) {
        selectedButtonsFromParams.push(1);
    }
    
    // Second catalog
    if (params?.simplify || params?.noise_removal || params?.translate) {
        selectedButtonsFromParams.push(2);
    }

    // Third catalog
    if (params?.proj_pipeline || params?.force_pdal) {
        selectedButtonsFromParams.push(3);
    }
    const [selectedButtons, setSelectedButtons] = useState<number[]>(selectedButtonsFromParams);
    const [loading, setLoading] = useState<boolean>(false);
    // const [selectedConversionDetails, setSelectedConversionDetails] = useState<ConversionFormDetails>(initialConversionDetails);
    const [selectedConversionDetails, setSelectedConversionDetails] = useState<ConversionFormDetails>({
        ...initialConversionDetails,
        ...params
    });

    const handleButtonClick = (buttonId: number) => {
        const index = selectedButtons.indexOf(buttonId);
        if (index === -1) {
            setSelectedButtons([...selectedButtons, buttonId]);
        } else {
            setSelectedButtons(selectedButtons.filter(id => id !== buttonId));
        }
    };

    const handleConversionData = (preventLoadingEnd? : boolean) : Promise<boolean> => {
        return new Promise((resolve, reject) => {
            setLoading(true)
            // const { reprojection, ...rest } = selectedConversionDetails;
            const { reprojection, proj_pipeline, simplify, noise_removal, translate, polygon_bounds, ...rest } = selectedConversionDetails;

            const isGeometrySelected = selectedButtons.includes(2);
            const isAdvancedSelected = selectedButtons.includes(3);

            // const updatedParams = isGeometrySelected
            // ? { ...rest }
            // : { ...rest, translate: undefined };
            const updatedParams = {
                ...rest,
                ...(isGeometrySelected ? {
                    simplify: simplify,
                    noise_removal: noise_removal,
                    translate: translate
                } : {
                    simplify: false,
                    noise_removal: false,
                    translate: undefined
                }),
                ...(isAdvancedSelected ? {
                    proj_pipeline: proj_pipeline
                } : {
                    proj_pipeline: "" 
                }),
                polygon_bounds 
            };

            const isReprojectionEmpty = Object.values(selectedConversionDetails.reprojection).every(value => value === "" || value === 'None');
            const updatedConversionDetails = isReprojectionEmpty ? updatedParams : selectedConversionDetails;

            // Convert string to number for translate        
            if(updatedConversionDetails?.translate && selectedButtons.includes(2)) {
                updatedConversionDetails.translate[0] = parseFloat(updatedConversionDetails.translate[0] as string);
                updatedConversionDetails.translate[1] = parseFloat(updatedConversionDetails.translate[1] as string);
                updatedConversionDetails.translate[2] = parseFloat(updatedConversionDetails.translate[2] as string);
            }

            updateParams(data.data_id, data.owner_id, updatedConversionDetails).then(() => {
                updateItemStore(data.data_id, {
                    params: {
                        ...data.params,
                        ...updatedConversionDetails
                    }
                })
                onChanged(data.data_id)
                message.success('Conversion details updated successfully');
                resolve(true);
            }).catch((error) => {
                message.error('Failed to update conversion details');
                reject(error);
                console.error(error);
            }).finally(() => {
                if(!preventLoadingEnd) setLoading(false)
            });
        });
    };

    const customExpandIcon = ({ isActive }: { isActive?: boolean }) =>
        isActive ? <KeyboardArrowUpIcon className='expand_icon' /> : <KeyboardArrowDownIcon className='expand_icon' />;

    const handleRunConversion = async () => { 
        setLoading(true);

        if(!data?.batch){
            let isParamsSaved = false;
            try {
                isParamsSaved = await handleConversionData(true);
            }catch(error) {
                if(typeof error === "string") {
                    message.error(error, 2);
                }else {
                    message.error('Failed to save parameters before conversion', 2);
                }
                console.error(error);
                setLoading(false);
                return;
            }
        }
        
        processData(data.data_id, data.owner_id).then((res) => {
            loadDataItem(data.data_id, data.owner_id).then((res) => {
                const newData = JSON.parse(res);
                updateItemStore(data.data_id, newData);
                message.success('Data converted successfully', 2);
            }).catch((error) => {
                if(typeof error === "string") {
                    message.error(error, 2);
                    return;
                }
                message.error(error?.message || error.errMessage || 'Failed to load data after starting conversion', 2);
            }).finally(() => {
                setLoading(false);
                onCancel();
            });
        }).catch((error) => {
            if (typeof error === "string") {
                message.error(error,2);
            } else {
                message.error(error?.message || 'Failed to start conversion',2);
            }
        }).finally(() => {
            setLoading(false);
        });
    }
    return (
        <DataIndexMenuEditModalContainer>
            <>
                <div className="conversation__container">
                    <Form onFinish={() => {
                        handleConversionData(false)
                    }}>
                        <div className='conversation__inner__block'>
                            <div className='conversation_left_block'>
                                {conversionMenuButtons.map((button: ButtonItem) => (
                                    <div key={button.id}>
                                        <Button
                                            onClick={() => handleButtonClick(button.id)}
                                            className={selectedButtons.includes(button.id) ? 'conversation_menu_btn selected' : 'conversation_menu_btn'}
                                        >
                                            {button.label}
                                        </Button>
                                    </div>
                                ))}
                            </div>

                            <div className='conversation_right_block'>
                                <Collapse
                                    accordion
                                    expandIconPosition="end"
                                    expandIcon={customExpandIcon}
                                >
                                    {selectedButtons.map((buttonId: number) => {
                                        const button = conversionMenuButtons.find(button => button.id === buttonId);
                                        if (!button) return null;
                                        return (
                                            <Panel
                                                header={button.label || ''}
                                                key={buttonId}
                                                className="custom-panel"
                                            >
                                                <div className='panel_data_form'>
                                                    {button.id === 1 && <ReprojectionMenuDetails selectedConversionDetails={selectedConversionDetails} setSelectedConversionDetails={setSelectedConversionDetails} data={data} />}
                                                    {button.id === 2 && <GeometryMenuDetails selectedConversionDetails={selectedConversionDetails} setSelectedConversionDetails={setSelectedConversionDetails} data={data} />}
                                                    {button.id === 3 && <AdvanceMenuDetails selectedConversionDetails={selectedConversionDetails} setSelectedConversionDetails={setSelectedConversionDetails} data={data} />}
                                                </div>
                                            </Panel>
                                        )

                                    })}
                                </Collapse>
                            </div>
                        </div>
                        <div className='footer_box'>
                            {data.locked && <div className='message-info'>Item is locked, please unlock to update parameter or run conversion</div>}
                            <div className='footer_inner_box'>
                                <Button htmlType="submit" disabled={data.locked} className='footer_btn footer_btn_save' loading={loading}>Save</Button>
                                <Button loading={loading} disabled={data.locked && !data.batch} className='footer_btn footer_btn_save' onClick={handleRunConversion}>Run Conversion</Button>
                                <Button className='footer_btn footer_btn_cancel' onClick={() => onCancel()}>Cancel</Button>
                            </div>
                        </div>
                    </Form>
                </div>
            </>
        </DataIndexMenuEditModalContainer>

    );
}

export default ConversionDetails;
